import { AccessInviteState, AccessLink, AccessParty, AccessStatus, GetAccessListRawResponse, RawAccess } from "./types";

const normaliseStatus = (status?: string): AccessStatus => (String(status).toUpperCase() === "CONNECTED" ? "CONNECTED" : "PENDING");

// The backend (AccessEntityHydrator) derives the pending sub-state from the owner's registration
// status and emits it as `invite_state`. INVITED = owner provisioned but not yet
// registered; AWAITING_APPROVAL = existing client yet to approve. Fall back to AWAITING_APPROVAL
// for older payloads that don't carry the field.
const normaliseInviteState = (raw: RawAccess): AccessInviteState =>
  String(raw.invite_state).toUpperCase() === "INVITED" ? "INVITED" : "AWAITING_APPROVAL";

/**
 * Pull the counterparty account off a raw link. The account DTO carries only
 * uuid/first_name/last_name/email/avatar; the display name is first+last, falling back to the
 * invitee email (top-level `email`) for an unregistered invitee whose name is still blank.
 */
const extractParty = (raw: RawAccess): AccessParty => {
  const account = raw.account ?? {};
  const name = [account.first_name, account.last_name].filter(Boolean).join(" ");
  const email = account.email ?? raw.email ?? "";
  return {
    uuid: account.uuid ?? "",
    name: name || email,
    email,
    avatar: account.avatar ?? null,
  };
};

export const transformAccess = (raw: RawAccess): AccessLink => ({
  id: raw.uuid ?? "",
  status: normaliseStatus(raw.status),
  inviteState: normaliseInviteState(raw),
  createdAt: raw.created_at ?? null,
  grantedAt: raw.accepted_at ?? null,
  party: extractParty(raw),
});

export const transformAccessList = (res: GetAccessListRawResponse): AccessLink[] => {
  // HAL omits `_embedded` when there are 0 results — default to [] for a clean empty state.
  const items = res?._embedded?.access ?? [];
  return items.map(transformAccess);
};
